import { SwapRightOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  DatePicker,
  Divider,
  Flex,
  InputNumber,
  Row,
  Space,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import moment from 'moment';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { PREFIX } from '@app/constants';
import { useContentWidth, useWindowSize } from '@app/hooks';
import {
  CHF_BIRTHDAY,
  CHF_CLIENTS_FILTER,
  CHF_DATE_END,
  CHF_DATE_START,
} from '@app/pages/Clients/ClientsList/constants/filterKeysAndIds';
import { ClientFiltersContext, NotificationContext } from '@app/providers';
import { correctRangeValues } from '@app/utils';

import {
  handleDateFilters,
  hideFilterBlock,
} from '@pages/Clients/ClientsList/utils';

import {
  NEXT_MONTH,
  NEXT_MONTH_LABEL,
  NEXT_N_DAYS,
  NEXT_N_DAYS_LABEL,
  NEXT_THREE_MONTHS,
  NEXT_THREE_MONTHS_LABEL,
  NEXT_WEEK,
  NEXT_WEEK_LABEL,
  TODAY,
  TODAY_LABEL,
  TOMORROW,
  TOMORROW_LABEL,
} from '@pages/Clients/ClientsList/constants/periods';

import type { NClientsList } from '@pages/Clients/ClientsList/ClientsList.types';

import '@pages/Clients/ClientsList/styles/DatesRange.desktop.scss';

const { Text } = Typography;

const currentYear = moment().format('YYYY');
const firstYearDay = `${currentYear}-01-01`;
const lastYearDay = `${currentYear}-12-31`;

const TipByAgeFilter = (
  <div className="tip-by-age-filter">
    <Text type="secondary">
      Для фильтрации по году используйте фильтр «Возраст»
    </Text>
  </div>
);

export const ClientsBirthday = ({
  includedFiltersIndex,
}: {
  includedFiltersIndex: number;
}) => {
  const {
    filters,
    dateFormatList,
    setFilters,
    handleDisabling,
    setUpServerDataChunk,
  } = useContext(ClientFiltersContext);
  const { showNotification } = useContext(NotificationContext);
  const { innerWidth } = useWindowSize();
  const { contentWidth } = useContentWidth();

  const today = useMemo(() => new Date(), []);

  const [fastFilter, setFastFilter] = useState<NClientsList.Period | null>(
    TODAY
  );
  const [nextNDays, setNextNDays] = useState<number | null>(null);
  const [isDatesSegmentMode, setIsDatesSegmentMode] = useState(false);
  const [customPeriodError, setCustomPeriodError] = useState<string | null>('');
  const [isButtonDisabledLocally, setIsButtonDisabledLocally] = useState(false);

  const inputRef = useRef<HTMLInputElement | null>(null);

  const { DateStart = null, DateEnd = null } =
    filters.ClientsFilter?.[includedFiltersIndex]?.Values?.[0];

  const startDate: string | undefined = useMemo(() => {
    if (DateStart === undefined) return undefined;

    return DateStart ? dayjs(DateStart).format() : undefined;
  }, [DateStart]);

  const endDate: string | undefined = useMemo(() => {
    if (DateEnd === undefined) return undefined;

    return DateEnd ? dayjs(DateEnd).format() : undefined;
  }, [DateEnd]);

  useEffect(() => {
    const isFreeRangeSegment = fastFilter !== NEXT_N_DAYS;

    if ((!startDate || !endDate) && isFreeRangeSegment) setFastFilter(null);
  }, [startDate, endDate, fastFilter]);

  useEffect(() => {
    const isFastFilter = fastFilter === NEXT_N_DAYS;

    !isFastFilter && setNextNDays(null);
  }, [fastFilter]);

  const updateDateFilters = useCallback(
    (startDate: string | null, endDate: string | null) => {
      handleDateFilters({
        startDate,
        endDate,
        targetFiltersAreaId: CHF_CLIENTS_FILTER,
        filterGroupIndex: includedFiltersIndex,
        filters,
        setFilters,
      });
    },
    [includedFiltersIndex, filters, setFilters]
  );

  useEffect(() => {
    correctRangeValues({
      inputStart: startDate,
      inputEnd: endDate,
      cbToSaveBothValues: ([localStartDate, localEndDate]) =>
        updateDateFilters(
          localStartDate
            ? dayjs(localStartDate).format(dateFormatList[1])
            : null,
          localEndDate ? dayjs(localEndDate).format(dateFormatList[1]) : null
        ),
      showNotification,
    });
  }, [
    startDate,
    endDate,
    dateFormatList,
    showNotification,
    setFilters,
    updateDateFilters,
  ]);

  const clearDateInput = useCallback(
    (key: NClientsList.DateKey): void => {
      const handledKey =
        filters?.[CHF_CLIENTS_FILTER]?.[includedFiltersIndex]?.Values?.[0]?.[
          key
        ];

      if (handledKey) {
        const [{ DateStart, DateEnd }] =
          filters?.[CHF_CLIENTS_FILTER]?.[includedFiltersIndex]?.Values;

        updateDateFilters(
          key === CHF_DATE_START ? null : DateStart,
          key === CHF_DATE_END ? null : DateEnd
        );
      }

      setIsDatesSegmentMode(false);
    },
    [includedFiltersIndex, filters, updateDateFilters]
  );

  const handleClickByFreeValueSegment = useCallback(() => {
    const hasEmptyFreeValue = !nextNDays && nextNDays !== 0;

    if (hasEmptyFreeValue) {
      updateDateFilters(null, null);
    }

    setIsDatesSegmentMode(true);
    setFastFilter(NEXT_N_DAYS);

    inputRef.current?.focus();
  }, [nextNDays, updateDateFilters]);

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ): void => {
    const isForbiddenSymbol = ['-', '+', ',', '.'].includes(event.key);

    if (isForbiddenSymbol) {
      event.preventDefault();
    }
  };

  const calculateMaxPeriodInDays = (): number => {
    const today = moment();
    const yearLastDay = moment().endOf('year');

    const maxPeriodInDays = yearLastDay.diff(today, 'days') + 1;
    return maxPeriodInDays;
  };

  const handleFreePeriodChange: ((value: number | null) => void) | undefined = (
    value
  ) => {
    const userNextDay = moment().add(value, 'days');
    const maxPeriodInDays = calculateMaxPeriodInDays();
    const isWrongPeriod = value && value > maxPeriodInDays;

    updateDateFilters(
      moment(today).format(dateFormatList[1]),
      moment(userNextDay).format(dateFormatList[1])
    );

    setNextNDays(value);

    if (isWrongPeriod) {
      setIsButtonDisabledLocally(true);
      return setCustomPeriodError(`Максимум: ${maxPeriodInDays} дн.`);
    }

    setIsButtonDisabledLocally(false);
    setCustomPeriodError(null);
  };

  return (
    <div
      className={`${PREFIX}chf-dates-range`}
      onClick={(e: MouseEvent | React.MouseEvent) => e.stopPropagation()}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          flexDirection: contentWidth <= 1000 ? 'column' : 'row',
          rowGap: contentWidth <= 1000 ? '16px' : 'auto',
        }}
      >
        <Space className="filter-content" direction="vertical">
          <Space size="small">
            <DatePicker
              className="date-input birth-day-input"
              placeholder={'от'}
              size="small"
              showNow={false}
              minDate={dayjs(firstYearDay, dateFormatList[1])}
              maxDate={dayjs(lastYearDay, dateFormatList[1])}
              renderExtraFooter={() => TipByAgeFilter}
              format={{
                format: 'DD.MM',
                type: 'mask',
              }}
              value={startDate === undefined ? null : dayjs(startDate)}
              onChange={(value) => {
                setIsDatesSegmentMode(false);

                if (value === null) {
                  clearDateInput(CHF_DATE_START);
                } else {
                  updateDateFilters(
                    value.format(dateFormatList[1]),
                    endDate ? dayjs(endDate).format(dateFormatList[1]) : null
                  );

                  nextNDays && setNextNDays(null);
                }
              }}
            />

            <SwapRightOutlined />

            <DatePicker
              className="date-input birth-day-input"
              placeholder={'до'}
              size="small"
              showNow={false}
              minDate={dayjs(firstYearDay, dateFormatList[1])}
              maxDate={dayjs(lastYearDay, dateFormatList[1])}
              renderExtraFooter={() => TipByAgeFilter}
              format={{
                format: 'DD.MM',
                type: 'mask',
              }}
              value={endDate === undefined ? null : dayjs(endDate)}
              onChange={(value) => {
                setIsDatesSegmentMode(false);

                if (value === null) {
                  clearDateInput(CHF_DATE_END);
                } else {
                  updateDateFilters(
                    startDate
                      ? dayjs(startDate).format(dateFormatList[1])
                      : null,
                    value.format(dateFormatList[1])
                  );

                  nextNDays && setNextNDays(null);
                }
              }}
            />
          </Space>

          <Divider className="date-inputs-divider" />

          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === TODAY ? 'primary' : 'text'
            }
            block
            onClick={() => {
              updateDateFilters(
                moment(today).format(dateFormatList[1]),
                moment(today).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(TODAY);
            }}
          >
            {TODAY_LABEL}
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === TOMORROW ? 'primary' : 'text'
            }
            block
            onClick={() => {
              const tomorrow: Date = new Date(today);

              tomorrow.setDate(tomorrow.getDate() + 1);
              updateDateFilters(
                moment(tomorrow).format(dateFormatList[1]),
                moment(tomorrow).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(TOMORROW);
            }}
          >
            {TOMORROW_LABEL}
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === NEXT_WEEK
                ? 'primary'
                : 'text'
            }
            block
            onClick={() => {
              const week = new Date(today);

              week.setDate(week.getDate() + 7);
              updateDateFilters(
                moment(today).format(dateFormatList[1]),
                moment(week).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(NEXT_WEEK);
            }}
          >
            {NEXT_WEEK_LABEL}
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === NEXT_MONTH
                ? 'primary'
                : 'text'
            }
            block
            onClick={() => {
              const month = new Date(today);

              month.setDate(month.getDate() + 30);
              updateDateFilters(
                moment(today).format(dateFormatList[1]),
                moment(month).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(NEXT_MONTH);
            }}
          >
            {NEXT_MONTH_LABEL}
          </Button>
          <Button
            size="small"
            type={
              isDatesSegmentMode && fastFilter === NEXT_THREE_MONTHS
                ? 'primary'
                : 'text'
            }
            block
            onClick={() => {
              const threeMonths = new Date(today);

              threeMonths.setDate(threeMonths.getDate() + 90);
              updateDateFilters(
                moment(today).format(dateFormatList[1]),
                moment(threeMonths).format(dateFormatList[1])
              );

              setIsDatesSegmentMode(true);
              setFastFilter(NEXT_THREE_MONTHS);
            }}
          >
            {NEXT_THREE_MONTHS_LABEL}
          </Button>
          <Flex gap={8} vertical>
            <Space onClick={handleClickByFreeValueSegment}>
              <Button
                block
                size="small"
                type={
                  isDatesSegmentMode && fastFilter === NEXT_N_DAYS
                    ? 'primary'
                    : 'text'
                }
              >
                {`${NEXT_N_DAYS_LABEL}:`}
              </Button>
              <InputNumber
                ref={inputRef}
                className="free-range-input"
                controls={false}
                size="small"
                type="number"
                value={nextNDays}
                onChange={handleFreePeriodChange}
                onKeyDown={handleKeyDown}
              />
            </Space>
            {customPeriodError && (
              <Alert message={customPeriodError} type="error" showIcon />
            )}
          </Flex>

          <Divider className="applying-button" />
          <Row justify="end">
            <Button
              type="primary"
              size="small"
              disabled={
                handleDisabling(
                  CHF_CLIENTS_FILTER,
                  CHF_BIRTHDAY,
                  false,
                  '',
                  includedFiltersIndex
                ) || isButtonDisabledLocally
              }
              onClick={() => {
                setUpServerDataChunk(
                  CHF_CLIENTS_FILTER,
                  CHF_BIRTHDAY,
                  false,
                  '',
                  includedFiltersIndex,
                  true
                );

                hideFilterBlock(
                  '.clients-birthday-label',
                  innerWidth,
                  includedFiltersIndex
                );
              }}
            >
              Выбрать
            </Button>
          </Row>
        </Space>
      </div>
    </div>
  );
};
